/**
 * useMessageList - React Query hook for fetching paginated message list
 *
 * Handles:
 * - Paginated message list with RDS offset-based pagination
 * - Filter integration
 * - Automatic refetch on filter changes
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { fetchMessageListV2 } from '../services/messageService';
import { messageQueryKeys } from '../config/queryKeys';
import { PAGINATION } from '../constants';
import { useMessageCase } from '../context';
/**
 * @param {Object} filters - Current filter state
 * @param {Object} pagination - { offset, limit, sort_order }
 * @param {Object} options - { enabled }
 * @returns {{ data, isLoading, error, refetch }}
 */
export function useMessageList(filters = {}, pagination = {}, options = {}) {
  const { enabled = true } = options;
  const { caseId } = useMessageCase();

  const {
    case_id = '',
    author_id = '',
    date_from = '',
    date_to = '',
    keyword = '',
    message_status = '',
    participants = '',
    sentiment = '',
  } = filters;

  const { offset = 0, limit = PAGINATION.DEFAULT_PAGE_SIZE, sort_order = 'desc' } = pagination;

  const queryKey = messageQueryKeys.messages.list({
    case_id: caseId || case_id,
    author_id,
    date_from,
    date_to,
    keyword,
    message_status,
    participants,
    sentiment,
    offset,
    limit,
    sort_order,
  });

  const query = useQuery({
    queryKey,
    queryFn: () =>
      fetchMessageListV2({
        case_id: caseId || case_id,
        author_id,
        date_from,
        date_to,
        keyword,
        message_status,
        participants,
        sentiment,
        limit,
        offset,
        sort_order,
      }),
    enabled,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    select: (response) => ({
      messages: response?.data?.message || [],
      count: response?.data?.count || 0,
      limit: response?.data?.limit || limit,
      offset: response?.data?.offset || offset,
      totalPages: Math.ceil((response?.data?.count || 0) / limit),
      currentPage: Math.floor(offset / limit) + 1,
    }),
  });

  return query;
}

/**
 * Hook to invalidate and refetch message list
 */
export function useInvalidateMessageList() {
  const queryClient = useQueryClient();

  return useCallback(() => {
    queryClient.invalidateQueries({ queryKey: messageQueryKeys.messages.all });
  }, [queryClient]);
}
